import dayjs from 'dayjs';

import { restApi } from '../../../api/restApiSlice';
import { type DiscoverabilityDayScope } from './discoverabilityDayScope';

// `previous` is `number | null` per the API contract. `null` means there is
// not enough historical data to compute a delta (the previous window predates
// the user's earliest activity); `0` means the previous window had zero
// activity. Both render as "N/A" but with different tooltips.
export type DiscoverabilityKpiMetric = {
  current: number;
  previous: number | null;
};

export type DiscoverabilityKpisResponse = {
  crawlersDetected: {
    current: number;
    previous: number | null;
    missing: string[];
  };
  pageCoverage: DiscoverabilityKpiMetric;
  errorRate: DiscoverabilityKpiMetric;
  // Whether any AI crawler has ever requested a page of this account's site
  // (account-wide and range-independent, unlike the metrics above). It is the
  // single source of truth for the "waiting for the first crawler visit"
  // placeholders — the daily-job status only drives the banner (PRE-3305).
  hasEverHadCrawlerActivity: boolean;
};

export type DiscoverabilityKpisQueryArgs = DiscoverabilityDayScope;

const discoverabilityKpisApi = restApi.injectEndpoints({
  endpoints: (builder) => ({
    getDiscoverabilityKpis: builder.query<DiscoverabilityKpisResponse, DiscoverabilityKpisQueryArgs>({
      query: (args) => ({
        url: '/v3/ai-insights/discoverability/kpis',
        method: 'GET',
        params: {
          dateFrom: args.dateFrom,
          dateTo: args.dateTo,
          crawlers: args.crawlers,
          domain: args.domain,
          timezone: args.timezone,
          // Send the browser locale's first day of week so week-granularity
          // buckets match the main dashboard's Error Rate KPI (PRE-2902),
          // matching DashboardV3API / CDNAnalyticsAPI.
          firstDayOfWeek: dayjs().localeData().firstDayOfWeek(),
        },
      }),
      providesTags: ['DiscoverabilityKpis'],
    }),
  }),
});

export { discoverabilityKpisApi };
export const { useGetDiscoverabilityKpisQuery } = discoverabilityKpisApi;
